import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BillingChargeService, CreditAction } from './billing-charge.service';
import { MceApiService, PublicUrlDto } from './mceapi.client.generated';

import { Report } from './report';
export { Report } from './report';


@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  private readonly billingChargeService = inject(BillingChargeService);
  private readonly mceApiService = inject(MceApiService);

  private readonly reportNames: Record<Report, string> = {
    [Report.CablesList]: 'Cable List',
    [Report.CableScheduleV1]: 'Cable Schedule V1',
    [Report.CableScheduleV2]: 'Cable Schedule V2',
    [Report.CableComments]: 'Cable Comments',
    [Report.CableSizing]: 'Cable Sizing',
    [Report.PowerLoss]: 'Power Loss',
    [Report.ProjectSummary]: 'Project Summary',
    [Report.ProjectCableSizing]: 'Project Cable Sizing',
    [Report.LoadList]: 'Load List',
    [Report.MTOCables]: 'MTO - Cables',
    [Report.VoltageDropMatrix]: 'Voltage Drop Matrix',
  };

  reportToDisplayName(report: Report): string {
    return this.reportNames[report];
  }

  reportToString(report: Report): string {
    switch (report) {
      case Report.CablesList: return 'CablesList';
      case Report.CableScheduleV1: return 'CableScheduleV1';
      case Report.CableScheduleV2: return 'CableScheduleV2';
      case Report.CableComments: return 'CableComments';
      case Report.CableSizing: return 'Calculation';
      case Report.LoadList: return 'LoadList';
      case Report.MTOCables: return 'MTOCables';
      case Report.PowerLoss: return 'PowerLoss';
      case Report.ProjectCableSizing: return 'ProjectCableSizing';
      case Report.ProjectSummary: return 'ProjectSummary';
      case Report.VoltageDropMatrix: return 'VoltageDropMatrix';
      default: return 'Undefined Report';
    }
  }

  doReport(report: Report, uids: string[]) {
    return this.mceApiService.v1ReportsDo({
      report: { value: this.reportToString(report) },
      uids
    });
  }

  async doChargedReport(report: Report, uids: string[]): Promise<PublicUrlDto | undefined> {
    const chargeQuote = await firstValueFrom(this.billingChargeService.getQuote({
      action: this.reportToChargeAction(report),
    }));

    const canProceed = await this.billingChargeService.ensureCanProceed(chargeQuote);
    if (!canProceed) return undefined;

    const reportUrl = await firstValueFrom(this.doReport(report, uids));
    await firstValueFrom(this.billingChargeService.applyCharge(chargeQuote));
    return reportUrl;
  }

  private reportToChargeAction(report: Report): CreditAction {
    switch (report) {
      case Report.CablesList: return CreditAction.ReportsCablesList;
      case Report.CableScheduleV1: return CreditAction.ReportCableScheduleV1;
      case Report.CableScheduleV2: return CreditAction.ReportCableScheduleV2;
      case Report.CableComments: return CreditAction.ReportCableComments;
      case Report.CableSizing: return CreditAction.ReportCableSizing;
      case Report.PowerLoss: return CreditAction.ReportPowerLoss;
      case Report.ProjectSummary: return CreditAction.ReportProjectSummary;
      case Report.ProjectCableSizing: return CreditAction.ReportProjectCableSizing;
      case Report.LoadList: return CreditAction.ReportLoadList;
      case Report.MTOCables: return CreditAction.ReportMTOCables;
      case Report.VoltageDropMatrix: return CreditAction.ReportVoltageDropMatrix;
      default: throw new Error(`No billing action is configured for report ${report}.`);
    }
  }

}
